import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";

/**
 * State machine + API glue for the "Try discovery on this domain"
 * mini-form in Admin → Ops.
 *
 * Owns:
 *  - form input state (website URL + extra seeds textarea)
 *  - the last-run result payload
 *  - the LRU-capped history table (fetched on mount + after each run)
 *  - the run/loading flag
 *
 * Extracted from AdminOps.jsx as part of the 2026-08-17 code review —
 * shrinks the parent from ~1600 lines and keeps the dry-run flow
 * testable in isolation.
 */
export function useDryRunPanel() {
  const [form, setForm] = useState({ website_url: "", seeds: "" });
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(() => {
    http.get("/admin/ops/discovery-dry-run/history")
      .then((r) => setHistory(r.data.rows || []))
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runDryRun = useCallback(async () => {
    const url = (form.website_url || "").trim();
    if (!url) { toast.error("Enter a website URL first."); return; }
    setBusy(true);
    setResult(null);
    try {
      const seeds = (form.seeds || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const { data } = await http.post("/admin/ops/discovery-dry-run", {
        website_url: url, extra_seed_urls: seeds,
      });
      setResult(data);
      loadHistory();
      if ((data.kept_total || 0) === 0) {
        toast.warning("Discovery kept 0 URLs — check the diagnostics panel below.");
      } else {
        toast.success(`Discovery kept ${data.kept_total} URL(s) in ${data.took_ms}ms.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    } finally { setBusy(false); }
  }, [form.website_url, form.seeds, loadHistory]);

  const applyHistoryRow = useCallback((row) => {
    // Click on a history row copies its URL into the form. We DO NOT
    // auto re-run — the operator decides when to retry.
    setForm({ website_url: row.website_url || "", seeds: "" });
    setResult(null);
  }, []);

  return {
    form, setForm,
    result, setResult,
    history,
    busy,
    runDryRun,
    applyHistoryRow,
  };
}
